/**
 * Format a date string for display with date and time
 */
export const formatClassificationDate = (dateString: string) => {
  const date = new Date(dateString);

  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  const formattedTime = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return { formattedDate, formattedTime, date };
};

/**
 * Get human-readable relative time. Handles BOTH past ("2 hours ago")
 * and future ("in 2 hours"). Used by <Timestamp format="relative"/> for
 * any time field — past (last run, created, updated) or future (next
 * run, scheduled at).
 */
export const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = Date.now();
  const diff = now - date.getTime();
  const absDiff = Math.abs(diff);
  const isFuture = diff < 0;
  const mins = Math.floor(absDiff / 60000);

  const phrase = (value: string): string =>
    isFuture ? `in ${value}` : `${value} ago`;

  if (mins < 1) {
    return 'just now';
  }

  if (mins < 60) {
    return phrase(`${mins} minute${mins !== 1 ? 's' : ''}`);
  }

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return phrase(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return phrase(`${days} day${days !== 1 ? 's' : ''}`);
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return phrase(`${months} month${months !== 1 ? 's' : ''}`);
  }

  const years = Math.floor(months / 12);
  return phrase(`${years} year${years !== 1 ? 's' : ''}`);
};

/**
 * Format a date for display (short format)
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Get the browser's resolved IANA timezone (e.g. "Africa/Johannesburg").
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Get the short timezone abbreviation for the browser's timezone (e.g. "SAST", "GMT").
 */
export const getUserTimezoneAbbreviation = (date: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZoneName: 'short',
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value ?? '';
  } catch {
    return '';
  }
};

/**
 * Difference in calendar days between an instant's local date and its UTC date.
 * Returns -1, 0, or +1 for any real-world timezone (offsets clamp to ±14h).
 */
const localDayOffsetFromUtc = (instant: Date): number => {
  const utcYmd = instant.toLocaleDateString('en-CA', { timeZone: 'UTC' });
  const localYmd = instant.toLocaleDateString('en-CA');
  const toEpoch = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toEpoch(localYmd) - toEpoch(utcYmd)) / 86_400_000);
};

/**
 * Given a UTC time of day in HH:MM, return the user's local equivalent as HH:MM
 * plus the local timezone abbreviation (e.g. "11:00 SAST").
 *
 * `dayOffset` is -1, 0, or +1 — non-zero when the local time falls on a
 * different calendar day than UTC, so callers can warn about day shifts.
 *
 * Returns null for malformed input so callers can decide how to handle.
 */
export const utcTimeToLocalPreview = (
  utcHHmm: string,
): { localHHmm: string; abbreviation: string; dayOffset: number } | null => {
  const match = utcHHmm.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  // Anchor on today so DST is reflected for the user's current state.
  const today = new Date();
  const utcDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      hours,
      minutes,
      0,
      0,
    ),
  );

  const localHHmm = utcDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return {
    localHHmm,
    abbreviation: getUserTimezoneAbbreviation(utcDate),
    dayOffset: localDayOffsetFromUtc(utcDate),
  };
};

/**
 * Render an absolute date+time with both UTC and local timezone shown,
 * suitable for a tooltip on a relative-time display.
 *
 * Same calendar day:    "23 Apr 2026, 09:15 UTC (11:15 SAST)"
 * Local crosses a day:  "23 Apr 2026, 23:15 UTC (24 Apr, 01:15 SAST)"
 */
export const formatAbsoluteWithZones = (
  value: string | Date | null | undefined,
): string | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;

  const utcDatePart = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const utcTime = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  const localTime = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const abbr = getUserTimezoneAbbreviation(date);

  // If the user's tz is UTC the two times are identical — only show once.
  if (utcTime === localTime) {
    return `${utcDatePart}, ${utcTime} UTC`;
  }

  // When local falls on a different calendar day, prefix the local time with
  // its date so the tooltip is unambiguous (common for US tz / early-UTC times).
  const dayOffset = localDayOffsetFromUtc(date);
  const localDatePrefix =
    dayOffset === 0
      ? ''
      : `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, `;

  return `${utcDatePart}, ${utcTime} UTC (${localDatePrefix}${localTime}${abbr ? ` ${abbr}` : ''})`;
};
